import { WalletBalance } from '@/components/billing/WalletBalance'
import { useJobillaOrgId } from '@/hooks'
import { Button, SubtleLoader } from '@/ui'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import {
	customerWalletShowQueryKey,
	invoiceBreakdownQueryKey,
	invoiceIndexQueryKey,
	InvoiceListItemData,
	invoiceSummaryQueryKey,
	useCampaignsShow,
	useCustomerValidateBillingDetails,
	useInvoiceBreakdown,
	useInvoicePay,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

type PayInvoiceDialogProps = {
	open: boolean
	onClose: () => void
	onPaid: () => void
	invoice: InvoiceListItemData
}

export function PayInvoiceDialog({ open, onClose, onPaid, invoice }: PayInvoiceDialogProps) {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()

	const breakdown = useInvoiceBreakdown(organisationId, invoice.id, {
		enabled: open && !!organisationId,
	})

	const billingValidation = useCustomerValidateBillingDetails(organisationId, undefined, {
		enabled: open && !!organisationId,
	})
	const taxPercentage = billingValidation.data?.customer?.tax?.taxRate.percentage ?? 0

	const campaignId = invoice.metadata.campaign_id ?? ''
	const campaign = useCampaignsShow(campaignId, {
		enabled: open && !!campaignId,
	})

	useEffect(() => {
		if (!open || !organisationId) { return }
		queryClient.invalidateQueries({ queryKey: invoiceBreakdownQueryKey(organisationId, invoice.id) })
		queryClient.invalidateQueries({ queryKey: customerWalletShowQueryKey(organisationId) })
	}, [open, organisationId, invoice.id, queryClient])

	const payMutation = useInvoicePay({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: invoiceIndexQueryKey(organisationId) })
			queryClient.invalidateQueries({ queryKey: invoiceSummaryQueryKey(organisationId) })
			queryClient.invalidateQueries({ queryKey: invoiceBreakdownQueryKey(organisationId, invoice.id) })
			queryClient.invalidateQueries({ queryKey: customerWalletShowQueryKey(organisationId) })
			onPaid()
		},
	})

	// The dialog stays mounted per row, so a previous failed attempt's error
	// would otherwise still be visible on reopen. Clear the mutation state
	// once the dialog has closed so the next open starts fresh.
	useEffect(() => {
		if (!open) {
			payMutation.reset()
		}
	}, [open, payMutation.reset])

	const data = breakdown.data
	const currencyFormatter = new Intl.NumberFormat(i18n.language, {
		style: 'currency',
		currency: (data?.currency ?? invoice.currency).toUpperCase(),
	})
	const formatMoney = (cents: number) => currencyFormatter.format(cents / 100)

	const campaignTitle = campaign.data?.title
	const campaignStart = campaign.data?.startDate ? parseISO(campaign.data.startDate) : null
	const campaignEnd = campaign.data?.endDate ? parseISO(campaign.data.endDate) : null
	const campaignDates = campaignStart && campaignEnd
		? `${format(campaignStart, 'd MMM yyyy')} – ${format(campaignEnd, 'd MMM yyyy')}`
		: campaignStart
		? format(campaignStart, 'd MMM yyyy')
		: null

	const walletApply = data ? Math.min(data.walletBalance, data.amountRemaining) : 0
	const successfulPayments = (data?.payments ?? []).filter((payment) => payment.status === 'paid')
	const errorMessage = payMutation.error?.response?.data?.message

	return (
		<AnimatePresence>
			{open && (
				<Dialog static open={open} onClose={onClose} className="relative z-50">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-black/30 backdrop-blur-sm"
					/>

					<div className="fixed inset-0 flex items-center justify-center p-4">
						<DialogPanel
							as={motion.div}
							initial={{ opacity: 0, scale: 0.95 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.95 }}
							className="bg-white rounded-lg shadow-xl max-w-xl w-full overflow-hidden"
						>
							<div className="p-6">
								<div className="flex items-start justify-between gap-4">
									<DialogTitle className="text-lg font-semibold text-gray-900">
										{t('settings.invoices.pay.title', {
											number: invoice.number ?? invoice.stripeInvoiceId,
										})}
									</DialogTitle>
									{data && (
										<div className="text-right shrink-0">
											<div className="text-xs uppercase tracking-wide text-grey-500">
												{t('settings.invoices.pay.walletBalanceLabel')}
											</div>
											<WalletBalance
												balanceCents={data.walletBalance}
												taxPercentage={taxPercentage}
												currencyFormatter={currencyFormatter}
												className="text-base font-semibold text-grey-900 tabular-nums"
											/>
										</div>
									)}
								</div>

								{(campaignTitle || campaignDates) && (
									<div className="mt-4 bg-grey-50 border border-grey-200 rounded-lg p-3 text-sm">
										{campaignTitle && (
											<div className="font-medium text-grey-900">{campaignTitle}</div>
										)}
										{campaignDates && (
											<div className="text-xs text-grey-500 mt-0.5">{campaignDates}</div>
										)}
									</div>
								)}

								<div className="mt-4">
									{breakdown.isError
										? (
											<div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm">
												<div className="font-semibold text-red-900 mb-1">
													{t('settings.invoices.pay.breakdownErrorTitle')}
												</div>
												<div className="text-red-700 mb-3">
													{t('settings.invoices.pay.breakdownErrorMessage')}
												</div>
												<Button
													type="subtle"
													onClick={() => breakdown.refetch()}
													isLoading={breakdown.isFetching}
												>
													{t('settings.invoices.pay.breakdownErrorRetry')}
												</Button>
											</div>
										)
										: breakdown.isPending || !data
										? <SubtleLoader delay={0} />
										: (
											<div className="bg-grey-50 border border-grey-200 rounded-lg p-4 text-sm">
												<div className="flex flex-col gap-2">
													{data.lineItems.map((item, index) => (
														<div
															key={`${item.description}-${index}`}
															className="flex justify-between gap-4"
														>
															<span className="text-grey-600 truncate">
																{item.description
																	|| t('settings.invoices.pay.lineItemFallback')}
															</span>
															<span className="text-grey-800 whitespace-nowrap">
																{formatMoney(item.amount)}
															</span>
														</div>
													))}

													<div className="border-t border-grey-200 my-1" />

													<div className="flex justify-between">
														<span className="text-grey-600">
															{t('settings.invoices.pay.subtotal')}
														</span>
														<span className="text-grey-800">
															{formatMoney(data.subtotal)}
														</span>
													</div>
													<div className="flex justify-between">
														<span className="text-grey-600">
															{t('settings.invoices.pay.tax')}
														</span>
														<span className="text-grey-800">{formatMoney(data.tax)}</span>
													</div>

													<div className="border-t border-grey-300 my-1" />

													<div className="flex justify-between font-semibold text-grey-900">
														<span>{t('settings.invoices.pay.total')}</span>
														<span>{formatMoney(data.total)}</span>
													</div>

													{successfulPayments.length > 0 && (
														<>
															<div className="border-t border-grey-200 my-1" />

															<div className="text-xs font-semibold text-grey-500 uppercase tracking-wide mt-1">
																{t('settings.invoices.pay.paymentsReceived')}
															</div>
															{successfulPayments.map((payment) => (
																<div
																	key={payment.id}
																	className="flex justify-between text-green-700"
																	title={payment.paidAt
																		? format(
																			new Date(payment.paidAt),
																			"MMM d, yyyy 'at' h:mm a",
																		)
																		: undefined}
																>
																	<span>{t('settings.invoices.pay.payment')}</span>
																	<span>-{formatMoney(payment.amount)}</span>
																</div>
															))}
														</>
													)}

													<div className="border-t border-grey-300 my-1" />

													<div className="flex justify-between font-semibold text-grey-900">
														<span>{t('settings.invoices.pay.amountDue')}</span>
														<span>{formatMoney(data.amountRemaining)}</span>
													</div>

													{!data.canPayViaWallet && data.amountRemaining > 0 && (
														<div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
															{t('settings.invoices.pay.insufficientFunds')}
														</div>
													)}

													{data.canPayViaWallet && walletApply < data.amountRemaining && (
														<div className="text-xs text-brand-700 bg-brand-50 border border-brand-200 rounded p-2 mt-1">
															{t('settings.invoices.pay.partialNotice', {
																remaining: formatMoney(
																	data.amountRemaining - walletApply,
																),
															})}
														</div>
													)}
												</div>
											</div>
										)}

									{errorMessage && (
										<div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
											{errorMessage}
										</div>
									)}
								</div>
							</div>

							<div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
								<Button type="subtle" onClick={onClose} disabled={payMutation.isPending}>
									{t('common.cancel')}
								</Button>
								<Button
									type="committal"
									onClick={() =>
										payMutation.mutate({ organisation: organisationId, invoice: invoice.id })}
									isLoading={payMutation.isPending}
									disabled={!data?.canPayViaWallet || payMutation.isPending}
								>
									{data
										? t('settings.invoices.pay.confirm', { amount: formatMoney(walletApply) })
										: t('settings.invoices.pay.confirmGeneric')}
								</Button>
							</div>
						</DialogPanel>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	)
}
